import React, { useState } from 'react';
import { usePortfolioContext } from './PortfolioContext';
import { StaleBanner, PendingRecomputeBanner, SettingsOutdatedBanner } from './StalenessIndicator';
import PortfolioGroupForm from './PortfolioGroupForm';
import PortfolioExport from './PortfolioExport';
import StalenessReview from './StalenessReview';
import apiService from '../../services/ApiService';
import TailwindModal from '../Tailwind/TailwindModal';
import TailwindButton from '../Tailwind/TailwindButton';
import { useNumberLocale, formatFixed } from '../../utils/numberFormat';
import { parseServerTimestamp } from '../../utils/dateFormat';
import { cn } from '../../lib/utils';

export default function PortfolioHeader() {
    const { selectedGroup, computing, computeGroup, selectGroup, refreshGroups, deselectGroup, isOwner, canWrite, pendingRecomputeCount } = usePortfolioContext();
    const numberLocale = useNumberLocale();
    const [showEdit, setShowEdit] = useState(false);
    const [showStalenessReview, setShowStalenessReview] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    if (!selectedGroup) return null;

    const staleCount = selectedGroup.assets.filter((a: any) => a.is_stale).length;
    // KPIs exist (group was computed at least once) but the backend nulled `computed_at`
    // because group settings (e.g. discount_rate) changed since — old KPIs are now obsolete.
    const isSettingsOutdated = !!selectedGroup.kpis && !selectedGroup.computed_at;
    const needsRecompute = pendingRecomputeCount > 0 || isSettingsOutdated;

    const handleDelete = async () => {
        await apiService.deletePortfolioGroup(selectedGroup.id);
        setShowDeleteModal(false);
        deselectGroup();
        refreshGroups();
    };

    return (
        <div className="bg-white border-b border-slate-200 px-6 py-4">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <h1 className="text-xl font-bold text-slate-900">{selectedGroup.name}</h1>
                    {selectedGroup.description && (
                        <p className="text-sm text-slate-500 mt-0.5">{selectedGroup.description}</p>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {canWrite && (
                        <button
                            onClick={() => setShowEdit(true)}
                            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                            title="Edit group"
                        >
                            <span className="material-symbols-outlined text-[20px]">edit</span>
                        </button>
                    )}
                    <button
                        onClick={() => setShowStalenessReview(true)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                        title="Portfolio history & approvals"
                    >
                        <span className="material-symbols-outlined text-[20px]">history</span>
                    </button>
                    {isOwner && (
                        <button
                            onClick={() => setShowDeleteModal(true)}
                            className="p-2 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-500 transition-colors"
                            title="Delete group"
                        >
                            <span className="material-symbols-outlined text-[20px]">delete</span>
                        </button>
                    )}
                    {selectedGroup.computed_at && (
                        <PortfolioExport groupId={selectedGroup.id} groupName={selectedGroup.name} />
                    )}
                    {canWrite && (
                        <button
                            onClick={computeGroup}
                            disabled={computing || selectedGroup.assets.length === 0}
                            title={
                                isSettingsOutdated
                                    ? 'Group settings changed — recompute required'
                                    : pendingRecomputeCount > 0
                                        ? `${pendingRecomputeCount} approval${pendingRecomputeCount !== 1 ? 's' : ''} awaiting recompute`
                                        : undefined
                            }
                            className={cn(
                                'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50',
                                needsRecompute
                                    ? 'bg-amber-600 hover:bg-amber-700 ring-2 ring-amber-300 ring-offset-1 animate-pulse'
                                    : 'bg-blue-600 hover:bg-blue-700',
                            )}
                        >
                            {computing ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Computing...
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-[18px]">calculate</span>
                                    Compute
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-slate-500">
                <span>{selectedGroup.asset_count} asset{selectedGroup.asset_count !== 1 ? 's' : ''}</span>
                <span>Discount rate: {formatFixed(selectedGroup.discount_rate * 100, numberLocale, 2)}%</span>
                {selectedGroup.computed_at && (
                    <span>Last computed: {parseServerTimestamp(selectedGroup.computed_at)?.toLocaleString()}</span>
                )}
            </div>
            <p className="text-xs text-slate-400 italic mt-1">
                Aggregates each asset's pro-rated cash flows from its last scenario compute (Financial &amp; Operational Model Editor inputs). Stale if an asset's scenario has changed since — see status.
            </p>

            {staleCount > 0 && (
                <div className="mt-3">
                    <StaleBanner staleCount={staleCount} onReview={() => setShowStalenessReview(true)} />
                </div>
            )}

            {pendingRecomputeCount > 0 && (
                <div className="mt-3">
                    <PendingRecomputeBanner
                        count={pendingRecomputeCount}
                        onCompute={computeGroup}
                        computing={computing}
                    />
                </div>
            )}

            {isSettingsOutdated && (
                <div className="mt-3">
                    <SettingsOutdatedBanner onCompute={computeGroup} computing={computing} />
                </div>
            )}

            {showEdit && (
                <PortfolioGroupForm
                    editGroup={selectedGroup}
                    onClose={() => setShowEdit(false)}
                    onCreated={() => { setShowEdit(false); selectGroup(selectedGroup.id); refreshGroups(); }}
                />
            )}

            {showStalenessReview && (
                <StalenessReview
                    initialAssetId={null}
                    onClose={() => { setShowStalenessReview(false); selectGroup(selectedGroup.id); }}
                />
            )}

            <TailwindModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Are you sure?"
                size="sm"
                footer={
                    <div className="flex justify-center gap-3 w-full">
                        <TailwindButton variant="ghost" icon="close" size="sm" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </TailwindButton>
                        <TailwindButton variant="danger" icon="delete_forever" size="sm" onClick={handleDelete}>
                            Yes, Delete
                        </TailwindButton>
                    </div>
                }
            >
                <p className="text-base text-center text-slate-700">
                    This will delete <strong>{selectedGroup.name}</strong> and all its content permanently.
                </p>
            </TailwindModal>
        </div>
    );
}
